import { DatePipe } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { HANDOVER_LOCATION_SECTION_ID } from 'src/app/handover-constants';
import {
  HandoverRelation,
  RelatedHandover,
} from 'src/app/interfaces/handover-relation';
import {
  Section,
  SectionComment,
  SectionsComments,
} from 'src/app/interfaces/section-comment';
import { DialogService } from 'src/app/services/dialog.service';
import { HandoverService } from 'src/app/services/handover.service';
import { NotificationService } from 'src/app/services/notification.service';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'app-handover-detail-section-comment',
  templateUrl: './handover-detail-section-comment.component.html',
  styleUrls: ['./handover-detail-section-comment.component.scss'],
  standalone: false,
})
export class HandoverDetailSectionCommentComponent implements OnInit {
  @Input() handoverId: number | undefined;
  @Input() readOnlyContent!: boolean;
  @Input() handoverRelation: HandoverRelation | undefined;
  @Output() showRedPointSectionsCommentsEmitter = new EventEmitter<boolean>();

  @ViewChild('commentEdit', { static: false })
  commentEdit!: ElementRef;

  sectionsComments: SectionsComments[] = [];
  sections: Section[] = [];
  handoverSectionId: number | undefined;
  comment = '';
  isEditingExistingComment = false;
  commentUnderEdition: SectionsComments | undefined;
  showUpdateWarning = false;
  isHOLOC = false;
  relatedHandover: RelatedHandover = {} as RelatedHandover;
  isLoading = false;

  constructor(
    public handoverService: HandoverService,
    private notificationService: NotificationService,
    private userService: UserService,
    public dialogService: DialogService,
    public datepipe: DatePipe
  ) {}

  ngOnInit(): void {
    if (this.handoverId) {
      this.setSectionsCommentsDataSource();
      this.setSectionsDataSource();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.handoverRelation && this.handoverRelation) {
      this.isHOLOC =
        this.handoverRelation.handoverLocationId === this.handoverId;
      if (
        (this.handoverRelation.handoverId === this.handoverId &&
          this.handoverRelation.handoverLocationId) ||
        (this.handoverRelation.handoverLocationId === this.handoverId &&
          this.handoverRelation.handoverId)
      )
        this.getRelatedHandoverData();
    }
  }

  getRelatedHandoverData(): void {
    if (this.handoverId) {
      this.isLoading = true;
      this.handoverService
        .getRelatedHandoverData(this.handoverId!)
        .subscribe((relatedHandover: RelatedHandover) => {
          this.relatedHandover = relatedHandover;
        });
      this.isLoading = false;
    }
  }

  setSectionsCommentsDataSource(): void {
    this.handoverService
      .getSectionsComments(this.handoverId!)
      .subscribe((sectionsComments: SectionsComments[]) => {
        if (
          !this.isHOLOC &&
          this.handoverRelation &&
          this.handoverRelation.handoverLocationId
        ) {
          // Si hay handover de locación reemplazo los comentarios de entrega de locación
          this.getLocationSectionCommentsFromLocationHandover(
            sectionsComments,
            this.handoverRelation.handoverLocationId
          );
        } else {
          this.sectionsComments = sectionsComments;
          this.showRedPointSectionsComments(this.sectionsComments.length > 0);
        }
      });
  }

  hasLocationHandover(handoverSectionId: number) {
    if (
      !this.isHOLOC &&
      this.handoverRelation &&
      this.handoverRelation.handoverLocationId &&
      handoverSectionId == HANDOVER_LOCATION_SECTION_ID
    ) {
      return true;
    } else return false;
  }

  getLocationSectionCommentsFromLocationHandover(
    sectionsComments: SectionsComments[],
    handoverLocationId: number
  ) {
    const locationOrigCmmt = sectionsComments.findIndex(
      (comment) => comment.handoverSectionId == HANDOVER_LOCATION_SECTION_ID
    );
    this.handoverService
      .getSectionsComments(handoverLocationId)
      .subscribe((locationSectionComments: SectionsComments[]) => {
        const locationNewCmmt = locationSectionComments.findIndex(
          (comment) => comment.handoverSectionId == HANDOVER_LOCATION_SECTION_ID
        );

        if (locationOrigCmmt >= 0) sectionsComments.splice(locationOrigCmmt, 1);

        if (locationNewCmmt >= 0)
          sectionsComments = this.orderByDateLocationSectionComments(
            sectionsComments,
            locationSectionComments[locationNewCmmt]
          );
        this.sectionsComments = sectionsComments;
        this.showRedPointSectionsComments(this.sectionsComments.length > 0);
      });
  }

  orderByDateLocationSectionComments(
    array: SectionsComments[],
    nuevoRegistro: SectionsComments
  ): SectionsComments[] {
    // Encontrar el índice donde insertar el nuevo registro
    let i = 0;
    while (i < array.length && array[i].date <= nuevoRegistro.date) {
      i++;
    }

    // Crear una nueva copia del array para evitar modificar el original
    const newArray = [...array];
    newArray.splice(i, 0, nuevoRegistro);
    return newArray;
  }

  setSectionsDataSource(): void {
    this.handoverService
      .getSections(this.handoverId!)
      .subscribe((sections: Section[]) => {
        this.sections = sections;
      });
  }

  saveSectionComment(): void {
    if (!this.handoverSectionId) {
      this.notificationService.notify('Debe seleccionar una sección.', false);
    } else if (this.comment.trim() == '') {
      this.notificationService.notify('Debe escribir un comentario.', false);
    } else {
      const dialogRef = this.dialogService.openConfirmDialog(
        'Comentarios',
        '¿Desea guardar el comentario?'
      );
      dialogRef.afterClosed().subscribe((res) => {
        if (res) {
          const sectionComment = {
            handoverId: this.handoverId,
            handoverSectionId: this.handoverSectionId,
            userId: this.userService.getUserProperties().userId,
            date: this.datepipe.transform(Date.now(), 'yyyy-MM-dd HH:mm:ss'),
            comment: this.comment,
          } as SectionComment;
          if (this.commentUnderEdition) {
            sectionComment.lastEditionDate =
              this.commentUnderEdition.lastEditionDate;
          }
          this.handoverService
            .saveSectionComment(sectionComment)
            .subscribe((comment: SectionComment) => {
              if (comment && !comment.alreadyWritten) {
                this.handoverSectionId = undefined;
                this.comment = '';
                this.isEditingExistingComment = false;
                this.setSectionsDataSource();
                this.setSectionsCommentsDataSource();
                this.notificationService.notify(
                  'El comentario se ha guardado con éxito.',
                  true
                );
              } else if (comment && comment.alreadyWritten) {
                this.showUpdateWarning = true;
                this.commentUnderEdition = {
                  handoverId: this.handoverId,
                  handoverSectionId: this.handoverSectionId,
                } as SectionsComments;
              } else {
                this.notificationService.notify(
                  'Ocurrió un error al grabar el comentario.',
                  false
                );
              }
            });
        }
      });
    }
  }

  delete(sectionComment: SectionsComments): void {
    const dialogRef = this.dialogService.openConfirmDialog(
      'Comentarios',
      `¿Desea eliminar el comentario de la sección ${sectionComment.handoverSectionName}?`
    );
    dialogRef.afterClosed().subscribe((res) => {
      this.handoverService
        .deleteSectionComment(
          sectionComment.handoverId,
          sectionComment.handoverSectionId
        )
        .subscribe((res) => {
          if (res) {
            this.setSectionsDataSource();
            this.setSectionsCommentsDataSource();
            this.notificationService.notify(
              'El comentario se ha eliminado con éxito.',
              true
            );
          } else {
            this.notificationService.notify(
              'Ocurrió un error al eliminar el comentario.',
              false
            );
          }
        });
    });
  }

  showRedPointSectionsComments(show: boolean): void {
    this.showRedPointSectionsCommentsEmitter.emit(show);
  }

  edit(comment: SectionsComments): void {
    this.commentUnderEdition = comment;
    this.handoverSectionId = comment.handoverSectionId;
    this.comment = comment.comment;
    this.isEditingExistingComment = true;
    this.commentEdit.nativeElement.focus();
  }

  discardEdition(): void {
    this.showUpdateWarning = false;
    this.commentUnderEdition = undefined;
    this.handoverSectionId = undefined;
    this.comment = '';
    this.isEditingExistingComment = false;
  }

  updateComment(): void {
    if (this.commentUnderEdition) {
      this.handoverService
        .getSectionComment(
          this.commentUnderEdition.handoverId,
          this.commentUnderEdition.handoverSectionId
        )
        .subscribe((comment) => {
          if (comment) {
            this.edit(comment);
          }
          this.showUpdateWarning = false;
        });
    }
  }
}
